import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { toast } from '@/lib/notifications';
import type { Invoice, TenantId } from '@etiquetachef/types';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { resendInvoiceEmailCallable } from '@/lib/callables';
import { trackEvents } from '@/lib/events';

type Props = {
  open: boolean;
  tenantId: TenantId;
  invoice: Invoice | null;
  onClose: () => void;
};

/**
 * Confirma o reenvio do e-mail `INVOICE_CREATED` de uma fatura ao OWNER do
 * tenant (`resendInvoiceEmail`).
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200 (Sprint 16).
 *
 * Contrato: chama `resendInvoiceEmailCallable({ tenantId, invoiceId })`;
 * sucesso → telemetria `invoiceEmailResent`, toast e fecha; erro →
 * `err.message` num `Alert` dentro do modal (#1021). `invoice === null` não renderiza. Não altera a
 * fatura: o servidor só dispara o e-mail (SendGrid, com idempotência por
 * segundo — ver `apps/functions/src/admin/resendInvoiceEmail.ts`). O
 * destinatário é sempre o OWNER do tenant; a tela não escolhe e-mail.
 * Permissão no servidor: `requireTenantOperator` com
 * `staffRoles: ADMIN_PERMISSIONS.resendInvoiceEmail` (ROOT/FINANCE/OPS —
 * `docs/permissions.md`, Billing; #795 ADM-7) ou usuário do CRM com escrita
 * no tenant (#776).
 *
 * Usado por: `InvoiceActions` (em `routes/InvoiceDetailPage.tsx`, rota
 * `/billing/invoices/:tenantId/:invoiceId`) e `routes/InvoicesPage.tsx`
 * (menu da linha; rotas `/billing/invoices[/:tenantId]`, menu Billing →
 * Faturas), admin web. Spec: `docs/screens/billing-invoices.md`.
 *
 * Histórico:
 *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function ResendInvoiceEmailDialog({
  open,
  tenantId,
  invoice,
  onClose,
}: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  if (!invoice) return null;

  /** Limpa o erro e devolve o controle ao pai. */
  function close(): void {
    setError(null);
    onClose();
  }

  /** Dispara o reenvio e registra a telemetria `invoiceEmailResent`. */
  async function handleSubmit(): Promise<void> {
    if (!invoice) return;
    setSubmitting(true);
    setError(null);
    try {
      await resendInvoiceEmailCallable({ tenantId, invoiceId: invoice.id });
      trackEvents.invoiceEmailResent(tenantId, invoice.id);
      toast.success(t('billing.resendInvoice.success'));
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AdminConfirm
      visible={open}
      title={t('billing.resendInvoice.title')}
      confirmLabel={t('billing.resendInvoice.confirm')}
      cancelLabel={t('tenantDetail.actions.cancel')}
      loading={submitting}
      onConfirm={(): void => {
        void handleSubmit();
      }}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {t('billing.resendInvoice.description', { invoice: invoice.invoice_number })}
        </Box>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </AdminConfirm>
  );
}
